import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, HelpIcon, Icon, SimplePagination, Spinner, Table, } from '@dixa/lib-shared-habla';
import { DashboardQueuesType } from 'lib-shared-legacy-types';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { analytics } from 'app/utils/segment';
import NO_RESULT_IMAGE from 'images/no-search-results.png';
import { bindActionToPromise } from 'utils/sagaApiGeneratorHelpers';
import { actions as personalActions } from '../../../settings/actions/personal';
import { QueuesItem } from './QueuesItem/QueuesItem';
import styles from './queuesOverview.module.scss';
import { VISIBLE_QUEUES_OVERVIEW_COLUMNS } from './shared';
const MESSAGE_BUTTON_VARIANT = 'primary';
const Message = (props) => {
    return (_jsxs("div", Object.assign({ className: styles.messageContainer }, { children: [props.before && (_jsx("div", Object.assign({ className: styles.messageBefore }, { children: props.before }))), _jsx("div", Object.assign({ className: styles.messageText }, { children: props.text })), props.after && _jsx("div", Object.assign({ className: styles.messageAfter }, { children: props.after }))] })));
};
const TABLE_HEAD = (_jsx(Table.Thead, { children: _jsx(Table.Tr, { children: VISIBLE_QUEUES_OVERVIEW_COLUMNS.map((column) => {
            const { label, icon, help, type, style } = column;
            return (_jsx(Table.Th, Object.assign({ style: style, className: styles[type] }, { children: _jsxs("div", Object.assign({ className: styles.tableHeadLabelContainer }, { children: [icon ? _jsx(Icon, { name: icon }) : null, _jsx("div", Object.assign({ className: styles.tableHeadLabel }, { children: label })), (help === null || help === void 0 ? void 0 : help.headline) && (help === null || help === void 0 ? void 0 : help.description) && (_jsx(HelpIcon, { className: styles.tableHeadIcon, placement: "bottom", title: help.headline, text: help.description }))] })) }), type));
        }) }) }));
const LOADING_MESSAGE = (_jsx(Message, { text: "Getting queues ...", before: _jsx(Spinner, { size: 32 }) }));
const MESSAGE_NO_RESULT_IMAGE = (_jsx("img", { className: styles.messageImage, src: NO_RESULT_IMAGE, alt: "No result image" }));
const ERROR_MESSAGE = _jsx(Message, { text: "Error" });
const QueuesOverview = (props) => {
    const navigate = useNavigate();
    const goToNextPage = () => {
        const { meta: { nextPageParams }, } = props;
        if (!nextPageParams) {
            return;
        }
        const searchParams = new URLSearchParams(nextPageParams);
        navigate(`?${searchParams}`);
        analytics.dashboardNextPageClicked();
    };
    const goToPrevPage = () => {
        const { meta: { prevPageParams }, } = props;
        if (!prevPageParams) {
            return;
        }
        const searchParams = new URLSearchParams(prevPageParams);
        navigate(`?${searchParams}`);
        analytics.dashboardPreviousPageClicked();
    };
    const renderQueues = () => {
        const { favouriteQueueIds, meta, queues, personalAddToFavouriteQueueIds, personalRemoveFromFavouriteQueueIds, onQueuesTypeChange, } = props;
        const { ajaxAsyncStatus, nextPageParams, prevPageParams, currentPageParams: { type }, } = meta;
        if (ajaxAsyncStatus === 'loading') {
            return LOADING_MESSAGE;
        }
        if (ajaxAsyncStatus === 'failed') {
            return ERROR_MESSAGE;
        }
        const renderedQueues = queues.map((queue) => {
            const { queue_id } = queue;
            const isFavourite = favouriteQueueIds.includes(queue_id);
            const toggleFavouriteStatus = isFavourite
                ? () => {
                    personalRemoveFromFavouriteQueueIds({ queueId: queue_id });
                    analytics.dashboardQueueItemFavoriteToggled({ value: false });
                }
                : () => {
                    personalAddToFavouriteQueueIds({ queueId: queue_id });
                    analytics.dashboardQueueItemFavoriteToggled({ value: true });
                };
            return (_jsx(QueuesItem, { dashboardQueue: queue, isFavourite: isFavourite, toggleFavouriteStatus: toggleFavouriteStatus }, queue_id));
        });
        if (!renderedQueues.length && !(nextPageParams || prevPageParams)) {
            if (type === DashboardQueuesType.Favourite) {
                return (_jsx(Message, { text: "There are no favorite queues.", before: MESSAGE_NO_RESULT_IMAGE, after: _jsx(Button, Object.assign({ className: styles.messageButton, variant: MESSAGE_BUTTON_VARIANT, onClick: () => {
                            const type = DashboardQueuesType.Agent;
                            onQueuesTypeChange(type);
                            analytics.dashboardQueuesTypeChanged({
                                type,
                                location: 'favoritesMessageButton',
                            });
                        } }, { children: "See my queues" })) }));
            }
            if (type === DashboardQueuesType.Agent) {
                return (_jsx(Message, { text: "You are not assigned to any queues.", before: MESSAGE_NO_RESULT_IMAGE, after: _jsx(Button, Object.assign({ className: styles.messageButton, variant: MESSAGE_BUTTON_VARIANT, onClick: () => {
                            const type = DashboardQueuesType.All;
                            onQueuesTypeChange(type);
                            analytics.dashboardQueuesTypeChanged({
                                type,
                                location: 'queuesMessageButton',
                            });
                        } }, { children: "See all queues" })) }));
            }
            if (type === DashboardQueuesType.Archived) {
                return (_jsx(Message, { text: "There are no archived queues.", before: MESSAGE_NO_RESULT_IMAGE, after: _jsx(Button, Object.assign({ className: styles.messageButton, variant: MESSAGE_BUTTON_VARIANT, onClick: () => {
                            const type = DashboardQueuesType.All;
                            onQueuesTypeChange(type);
                            analytics.dashboardQueuesTypeChanged({
                                type,
                                location: 'archivedQueuesMessageButton',
                            });
                        } }, { children: "See all queues" })) }));
            }
        }
        const tableFoot = (!!nextPageParams || !!prevPageParams) && (_jsx(Table.Tfoot, { children: _jsx(Table.Tr, { children: _jsx(Table.Td, Object.assign({ colSpan: VISIBLE_QUEUES_OVERVIEW_COLUMNS.length }, { children: _jsx(SimplePagination, { previousButtonClick: goToPrevPage, nextButtonClick: goToNextPage, previousButtonDisabled: !prevPageParams, nextButtonDisabled: !nextPageParams, loading: ajaxAsyncStatus === 'loading' }) })) }) }));
        return (_jsxs(Table, Object.assign({ stickyHeader: true, stickyFooter: true, headerWithBackground: false, framed: false }, { children: [TABLE_HEAD, _jsx(Table.Tbody, { children: renderedQueues }), tableFoot] })));
    };
    return renderQueues();
};
const mapStateToProps = (state) => {
    const { dashboard, settings: { personal: { favouriteQueueIds }, }, } = state;
    return Object.assign(Object.assign({}, dashboard), { favouriteQueueIds });
};
const mapDispatchToProps = (dispatch) => {
    return {
        personalAddToFavouriteQueueIds: bindActionToPromise(dispatch, personalActions.personalAddToFavouriteQueueIds),
        personalRemoveFromFavouriteQueueIds: bindActionToPromise(dispatch, personalActions.personalRemoveFromFavouriteQueueIds),
    };
};
export default connect(mapStateToProps, mapDispatchToProps)(QueuesOverview);
